import { inject } from "@angular/core";
import { firstValueFrom, map } from "rxjs";
import { Tool } from "@aws-sdk/client-bedrock-runtime";
import { AITool } from "../../../03_model/ai-tool.interface";
import { AIComponentMessage, AIToolResponseMessage } from "../../../03_model/ai-chat-message.model";
import { ManufacturingOrderService } from "../../../07_feature/manufacturing-order/manufacturing-order.service";
import { OrderSearchResultMessageComponent } from "../messages/order-search-result-message/order-search-result-message.component";
import { I18n } from "@corporate/ng-tebis-base-components";

export class SearchOrderTool implements AITool {

  i18n = inject(I18n);

  tool_name = 'search_order';
  orderService = inject(ManufacturingOrderService);

  description = <Tool>{
    "toolSpec": {
      "name": this.tool_name,
      "description": "Search for manufacturing orders in the database by name. Orders often have the customer in their name, so searching by customer name can also be useful. The response includes the IDs and names of the matching orders.",
      "inputSchema": {
        "json": {
          "type": "object",
          "properties": {
            "name": {
              "type": "string",
              "description": "The name of the manufacturing order to search for in the database."
            }
          },
          "required": [
            "name"
          ]
        }
      }
    }
  };

  async invoke({name}: {name?: string}): Promise<AIToolResponseMessage> {
    if (!name) {
      return {
        type: 'tool-response',
        role: 'user',
        content: `Tool result: No name parameter provided for manufacturing order search.`,
        requiresFollowUp: true,
      };
    }
    try {
      const orders = await firstValueFrom(this.orderService.getOrders(null, 100, '', 'name', 'asc').pipe(map(page =>
        page.orders.filter(o => o.name.includes(name))
      )));

      return {
        type: 'tool-response',
        role: 'user',
        content: `Tool result: Found ${orders.length} manufacturing orders with name including "${name}": ${orders.map(o => `Name: ${o.name}, ID: ${o.id}`).join('; ')}. You can use the "display_orders" tool with the IDs of the orders to display them to the user.`,
        requiresFollowUp: true,
      };
    } catch (e) {
      return {
        type: 'tool-response',
        role: 'user',
        content: `Tool result: An error occurred while searching for manufacturing orders with name including "${name}". Error details: ${e}`,
        requiresFollowUp: true,
      };
    }
  }
}
